import React, { useState, useEffect } from 'react';
import { Button, Modal, ListGroup, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, ClockHistory } from 'react-bootstrap-icons';
import { getSectionContent, updateContent, getContentHistory, restoreContent } from '../services/contentService';
import ContentEditor from './editor/ContentEditor';
import SectionControls from './SectionControls';
import { useAuth } from '../contexts/SecurityContext';
import RoleGuard from './security/RoleGuard';
import './EditableContent.scss';

// Read the right content/version off a section item depending on which block
// (primary or secondary) this editor is bound to.
function readBlock(section, blockId) {
  if (!section) return { content: '', version: 1 };
  if (blockId === 'secondary') {
    return {
      content: section.secondaryContent || '',
      version: section.secondaryVersion || 1,
    };
  }
  return {
    content: section.content || '',
    version: section.version || 1,
  };
}

function EditableContent({
  pageId,
  section,
  onMoveUp,
  onMoveDown,
  onDelete,
  isFirstSection,
  isLastSection,
  blockId = 'primary',
  showSectionControls = true,
}) {
  const initial = readBlock(section, blockId);
  const [content, setContent] = useState(initial.content);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originalContent, setOriginalContent] = useState(initial.content);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [version, setVersion] = useState(initial.version);
  const [isSaving, setIsSaving] = useState(false);

  const { user } = useAuth();

  // Update content when section prop or blockId changes
  useEffect(() => {
    const next = readBlock(section, blockId);
    setContent(next.content);
    setOriginalContent(next.content);
    setVersion(next.version);
  }, [section, blockId]);

  // Fetch fresh content function (used when editing or refreshing)
  async function fetchContent() {
    setIsLoading(true);
    try {
      const sectionData = await getSectionContent(pageId, section.position);
      const next = readBlock(sectionData, blockId);
      setContent(next.content);
      setOriginalContent(next.content);
      setVersion(next.version);
    } catch (error) {
      console.error('Error fetching content:', error);
      const fallback = `<p>Content for position ${section.position} not found</p>`;
      setContent(fallback);
      setOriginalContent(fallback);
    } finally {
      setIsLoading(false);
    }
  }

  // Function to start editing with fresh content
  async function startEditing() {
    setIsEditing(true);
    await fetchContent();
  }

  async function saveContent() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';

      await updateContent(pageId, section.position, content, 'html', modifiedBy, {
        blockId,
        layout: section?.layout,
      });
      setOriginalContent(content);
      setIsEditing(false);
      // Refresh to get the new version number
      fetchContent();
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save content. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function cancelEditing() {
    setContent(originalContent);
    setIsEditing(false);
  }

  async function openHistory() {
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const historyData = await getContentHistory(pageId, section.position);
      // History entries may now include a blockId; only show ones for this block
      // (older entries without blockId are treated as 'primary' for back-compat).
      const filtered = (historyData || []).filter(
        (h) => (h.blockId || 'primary') === blockId
      );
      setHistory(filtered);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function restoreVersion(timestamp) {
    try {
      await restoreContent(pageId, section.position, timestamp);
      setShowHistory(false);
      fetchContent();
    } catch (error) {
      console.error('Error restoring content:', error);
      alert('Failed to restore content. Please try again.');
    }
  }

  if (isLoading) {
    return <div className="loading">Loading content...</div>;
  }

  return (
    <div className="editable-content">
      {isEditing ? (
        <div className="editor-container">
          <ContentEditor
            key={`editor-${pageId}-${section.position}-${blockId}`}
            value={content}
            onChange={setContent}
          />
          <div className="editor-actions">
            <Button
              variant="success"
              onClick={saveContent}
              className="save-btn"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <CheckLg /> Save
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={cancelEditing}
              className="cancel-btn"
              disabled={isSaving}
            >
              <XLg /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="content-container">
          <div
            className="content-formatting"
            dangerouslySetInnerHTML={{ __html: content }}
          />
          <RoleGuard role="admin">
            <div className="admin-controls">
              <Button
                variant="outline-secondary"
                size="sm"
                className="edit-btn"
                onClick={startEditing}
              >
                <PencilFill /> Edit
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="history-version-btn"
                onClick={openHistory}
                title="View content history"
              >
                <ClockHistory className="me-1" /> v{version}
              </Button>

              {showSectionControls && (
                <SectionControls
                  onMoveUp={onMoveUp}
                  onMoveDown={onMoveDown}
                  onDelete={onDelete}
                  isFirstSection={isFirstSection}
                  isLastSection={isLastSection}
                />
              )}
            </div>
          </RoleGuard>
        </div>
      )}

      {/* History Modal */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Content History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <p>Loading history...</p>
          ) : history.length === 0 ? (
            <p>No history available</p>
          ) : (
            <ListGroup>
              {history.map((item, index) => (
                <ListGroup.Item key={index} action onClick={() => restoreVersion(item.timestamp)}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Version {item.version}</strong>
                      <div>Modified by: {item.modifiedBy}</div>
                      <small>{new Date(item.timestamp).toLocaleString()}</small>
                    </div>
                    <Button size="sm" variant="outline-primary">Restore</Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistory(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EditableContent;
